import _extends from "@babel/runtime/helpers/esm/extends";
import { EModel } from './model';
import { fieldsUser } from './user';

/**
 * Enum representing the patient's birth sex.
 */
export let EPatientBirthSex;

/**
 * Enum representing the patient's race.
 */
(function (EPatientBirthSex) {
  EPatientBirthSex["Male"] = "M";
  EPatientBirthSex["Female"] = "F";
  EPatientBirthSex["Unknow"] = "UNK";
})(EPatientBirthSex || (EPatientBirthSex = {}));
export let EPatientRace;

/**
 * Enum representing the reasons a patient should not be contacted.
 */
(function (EPatientRace) {
  EPatientRace["AmericanIndianOrAlaskaNative"] = "american-indian-or-alaska-native";
  EPatientRace["Asian"] = "asian";
  EPatientRace["BlackOrAfricanAmerican"] = "black-or-african-american";
  EPatientRace["NativeHawaiianOrOtherPacificIslander"] = "native-hawaiian-or-other-pacific-islander";
  EPatientRace["White"] = "white";
  EPatientRace["OtherRace"] = "other-race";
})(EPatientRace || (EPatientRace = {}));
export let EDoNotContactReason;

/**
 * Enum representing the patient's ethnicity group.
 */
(function (EDoNotContactReason) {
  EDoNotContactReason["UI"] = "ui";
  EDoNotContactReason["Deceased"] = "deceased";
  EDoNotContactReason["Patient"] = "patient";
  EDoNotContactReason["IntegratorPreference"] = "integrator-preference";
  EDoNotContactReason["IntegratorStaff"] = "integrator-staff";
})(EDoNotContactReason || (EDoNotContactReason = {}));
export let EPatientEthnicityGroup;

/**
 * Enum representing the suffix for a patient's name.
 */
(function (EPatientEthnicityGroup) {
  EPatientEthnicityGroup["HispanicOrLatino"] = "hispanic-or-latino";
  EPatientEthnicityGroup["NotHispanicOrLatino"] = "not-hispanic-or-latino";
})(EPatientEthnicityGroup || (EPatientEthnicityGroup = {}));
export let EPatientSuffix;

/**
 * Enum representing the demographic code system (e.g., CDC).
 */
(function (EPatientSuffix) {
  EPatientSuffix["II"] = "II";
  EPatientSuffix["III"] = "III";
  EPatientSuffix["IV"] = "IV";
  EPatientSuffix["JR"] = "Jr.";
  EPatientSuffix["SR"] = "Sr.";
})(EPatientSuffix || (EPatientSuffix = {}));
export let EDemographicCodeSystem;

/**
 * Enum representing patient journey event types.
 */
(function (EDemographicCodeSystem) {
  EDemographicCodeSystem["cdc"] = "cdc";
})(EDemographicCodeSystem || (EDemographicCodeSystem = {}));
export let EPatientJourneyEventType;

/**
 * Enum representing the range of time for patient journey events.
 */
(function (EPatientJourneyEventType) {
  EPatientJourneyEventType["Appointments"] = "appointments";
  EPatientJourneyEventType["BillingTransactions"] = "billingTransactions";
  EPatientJourneyEventType["Diagnoses"] = "diagnoses";
  EPatientJourneyEventType["FeedbackResponses"] = "feedbackResponses";
  EPatientJourneyEventType["Insurances"] = "insurances";
  EPatientJourneyEventType["PatientForms"] = "patientForms";
  EPatientJourneyEventType["Procedures"] = "procedures";
  EPatientJourneyEventType["Recalls"] = "recalls";
  EPatientJourneyEventType["Referrals"] = "referrals";
})(EPatientJourneyEventType || (EPatientJourneyEventType = {}));
export let EPatientJourneyRangeBy;

/**
 * Type representing details of a patient's journey data.
 */
(function (EPatientJourneyRangeBy) {
  EPatientJourneyRangeBy["Years"] = "years";
  EPatientJourneyRangeBy["Days"] = "days";
})(EPatientJourneyRangeBy || (EPatientJourneyRangeBy = {}));
/**
 * Type representing an event in a patient's journey.
 */
/**
 * Type representing data for a patient's journey.
 */
/**
 * @see https://hl7.org/fhir/R5/valueset-account-type.html
 */
export let EBillingAccountType;

/**
 * Type representing a patient's billing account.
 */
(function (EBillingAccountType) {
  EBillingAccountType["PatientBalance"] = "patient-balance";
  EBillingAccountType["PatientCustomValue"] = "patient-custom-value";
})(EBillingAccountType || (EBillingAccountType = {}));
export let EPatientAlertPriority;
(function (EPatientAlertPriority) {
  EPatientAlertPriority["Normal"] = "normal";
  EPatientAlertPriority["High"] = "high";
  EPatientAlertPriority["Critical"] = "critical";
})(EPatientAlertPriority || (EPatientAlertPriority = {}));
/**
 * Interface representing a patient.
 */
export const fieldsPatient = _extends({}, fieldsUser, {
  alerts: {
    type: 'Object'
  },
  balance: {
    type: 'Number'
  },
  balanceUpdatedAt: {
    type: 'Date'
  },
  billingAccountNumber: {
    type: 'Object',
    array: true
  },
  billingAccounts: {
    type: 'Object',
    array: true
  },
  birthSex: {
    type: 'Option',
    values: EPatientBirthSex
  },
  culture: {
    type: 'Option',
    values: ['cn', 'hk']
  },
  deceased: {
    type: 'Boolean'
  },
  demographicEthnicity: {
    type: 'Object',
    array: true
  },
  demographicRace: {
    type: 'Object',
    array: true
  },
  doNotContactReason: {
    type: 'Option',
    values: EDoNotContactReason
  },
  ethnicityGroup: {
    type: 'Option',
    values: EPatientEthnicityGroup
  },
  externalContacts: {
    type: 'Object',
    array: true
  },
  externalRawSource: {
    type: 'Unknown'
  },
  lastProcessedAt: {
    type: 'Date'
  },
  maritalStatus: {
    type: 'Object'
  },
  medicalRecordNumber: {
    type: 'Text'
  },
  mergedInto: {
    type: 'ID',
    ref: EModel.Patient
  },
  outboundContact: {
    type: 'Object',
    array: true
  },
  preferredName: {
    type: 'Text'
  },
  prefix: {
    type: 'Text'
  },
  previousAddress: {
    type: 'Text'
  },
  primaryCareProvider: {
    type: 'ID',
    ref: EModel.Provider
  },
  race: {
    type: 'Option',
    values: EPatientRace
  },
  randomName: {
    type: 'Boolean'
  },
  recommendations: {
    type: 'Object'
  },
  searchIds: {
    type: 'Text',
    array: true
  },
  source: {
    type: 'Option',
    values: ['widget', 'integrator', 'ui', 'referring-provider', 'hub']
  },
  stats: {
    type: 'Object'
  },
  subscribers: {
    type: 'Object',
    array: true
  },
  suffix: {
    type: 'Option',
    values: EPatientSuffix
  },
  veteran: {
    type: 'Boolean'
  },
  widget: {
    type: 'Option',
    values: ['lumabot']
  }
});